/**
 * LiveStream — the run, as ONE stream.
 *
 * This is the surface the 2026-09-16 walkthrough asked for: Wright ran for
 * twenty-six minutes and the case file said "Working now" and nothing else.
 * The pane answers the three questions a person actually has while an agent is
 * working, in the order they ask them:
 *
 *   what is it doing RIGHT NOW   `.ls-now` — the newest event as a sentence
 *   has it stopped moving        `.ls-head` clock + `.ls-foot` turn count
 *   what has it done so far      `LiveTalk`, then the folded `<details>`
 *
 * The middle used to be a twelve-line mono log. It is a conversation now
 * (2026-09-17): what the agent said, rendered as prose; what it thought,
 * folded; each tool call as one row with its result. That is the same
 * grammar the CLI and the desktop feed read a session in, and it is what
 * "watch a colleague work" actually means. See `LiveTalk`.
 *
 * Everything below the conversation is FOLDED, because none of it answers the
 * live question. Opening one is the reader asking a second question, and each
 * fold is built out of a read that already exists: `useWorkRunView` for the
 * steps, `runCommits` for the branch, the runs roll-up for the earlier runs.
 *
 * A run is named by what it was for (`runLabel`), never by its attempt number:
 * `attempt_no` counts every run on the item, so "attempt 4" read as three
 * failures when it was the fourth run (2026-10-06). A planning run also draws
 * its ladder (`PlanningLadder`), because its beats arrive minutes apart; a
 * one-attempt lane run, which posts no beats, draws the stages its session
 * reports instead (`LaneProgress`).
 *
 * Three modes, one pane. While the attempt is live the bar is green, the clock
 * counts up (against the lane's budget when the run recorded one) and the dock
 * above offers the way to stop. Once it has stopped the bar is amber, the
 * clock freezes at the end, and the pane leads with WHY — in the stop
 * classifier's own words, plus what survived (the branch, the commits) and
 * what did not (the pull request). An infrastructure stop says so, because
 * "the sandbox was already used" is not a reason to rewrite a plan. The run
 * page also shows a run that finished well: the bar is a quiet green, the
 * pill says Done, and the pane leads with the run's last words.
 *
 * Nothing here is inferred. The tool-verb map turns `edit` into "Editing" and
 * stops there; an event kind with no words of its own prints none; a fact the
 * record does not carry (a repo, a model, a sandbox) is left out rather than
 * guessed. The events are keyed to ONE attempt (`useRunEvents({ runId })`), so
 * a second attempt can never show the first one's log.
 */
import { useEffect, useMemo, useState, type ReactNode, type RefObject } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Loader2 } from "lucide-react";
import type {
  CodingEvent,
  WorkItemRunListEntry,
  WorkRunStep,
  WorkRunViewResponse,
} from "@yagni/shared";
import {
  BUILD_CHECK_STEP_LABEL,
  BUILD_SETUP_STEP_COPY,
  BUILD_STOP_CAUSE_WORDS,
  isBuildSetupStep,
  isBuildStopCause,
  isPlanningMilestone,
  isSteeringNoteEvent,
  PLANNING_MILESTONE_COPY,
  runUrl,
  stopReasonClass,
} from "@yagni/shared";
import { durationLabel } from "../workers/copy.js";
import { useRunEvents } from "../hooks/useRunEvents.js";
import { stripMarkdownInline } from "../lib/markdown.js";
import { clampToWord } from "../lib/text.js";
import { buildEnvironmentLine, type BuildEnvironmentRecord } from "./buildEnvironment.js";
import { LiveTalk, TALK_TURN_LIMIT, TOOL_PAST, TOOL_VERB, talkTurnsOf } from "./LiveTalk.js";
import { LaneProgress, laneSessionStartOf, sessionAtOf, sessionEventsOf } from "./LaneProgress.js";
import { PlanningLadder } from "./PlanningLadder.js";
import { SetupLadder } from "./SetupLadder.js";
import { runCommits, stagePhrase, useWorkRunView, type RunCommit } from "./RunSurface.js";
import { runLabel } from "./runLabel.js";
import { caseRunStatusLabel, formatMillicents, LIVE_RUN_POLL_MS } from "./useWorkItemRuns.js";
import { useWorkItemEntityChange } from "./useWorkItemEntityChange.js";
import { useShowsRunCost } from "../hooks/useShowsRunCost.js";
import { steeringNoteLeftBy, steeringNoteWaitingLine } from "./workCopy.js";
import {
  fixingAsksClause,
  stopReasonWords,
  WORKING_CLAUSE,
  type PipelineStage,
} from "./workStage.js";
import "./liveStream.css";

/**
 * Live: a run in motion. Waiting: a run parked on a person (a plan waiting
 * for approval, a build parked on a budget), still this run but not moving.
 * Stopped: it ended without finishing. Done: it finished well.
 */
export type LiveStreamMode = "live" | "waiting" | "stopped" | "done";

export interface LiveStreamProps {
  workItemId: string;
  /** The run on the card, from the runs roll-up. Null while it is still loading. */
  run: WorkItemRunListEntry | null;
  /** Every run on the item, newest first. */
  runs: readonly WorkItemRunListEntry[];
  /** The agent whose attempt this is. Null keeps the clause neutral. */
  workerName: string | null;
  mode: LiveStreamMode;
  /** Where the work stands, for the working clause. */
  stage: PipelineStage;
  /** The item's pull request, when it opened one. */
  prNumber?: number | null;
  /** So the hand-off strip can scroll the pane into view. */
  paneRef?: RefObject<HTMLDivElement | null>;
  /**
   * The pane IS the run record (the run page): every fold opens, the whole
   * conversation shows, and there is no link to the page it is already on.
   */
  record?: boolean;
  /** More folds, after the built-in ones (the run page adds its own). */
  children?: ReactNode;
  /**
   * Which environment the build runs on, from the run's own record. The case
   * file passes it while a build runs; the run page prints it in its record.
   */
  environment?: BuildEnvironmentRecord | null;
  /**
   * Who speaks in a one-attempt lane run's sessions: its plan session is
   * Reeve's, every other one Wright's. Absent names `workerName` throughout.
   */
  laneSpeakers?: { plan: string | null; build: string | null } | null;
}

/**
 * The live heading's clause (YAG-1079). A build at work on its own pull
 * request in Review is a fix round, the builder's, whatever craft owns the
 * stage: "Wright is fixing 2 asks", not "Wright is reviewing". A lane run says
 * so by its session's stage; a legacy run by its round.
 */
export function liveClauseOf(
  stage: PipelineStage,
  run: Pick<WorkItemRunListEntry, "kind" | "sessionStage" | "fixRound" | "fixAsks"> | null,
  workerName: string | null,
): string {
  // Nothing is inferred before the runs roll-up names the run.
  if (!run) return WORKING_CLAUSE[stage](workerName);
  const ownRun = run.kind !== "review" && run.kind !== "qa";
  const fixing =
    run.sessionStage === "fix" ||
    (typeof run.fixRound === "number" && run.fixRound > 0) ||
    stage === "review";
  if (fixing && ownRun) return fixingAsksClause(workerName, run.fixAsks);
  return WORKING_CLAUSE[stage](workerName);
}

function streamHeading(
  mode: LiveStreamMode,
  candidateReady: boolean,
  clause: string,
  workerName: string | null,
  endClock: string | null,
) {
  if (mode === "live") return { label: "Live", sentence: clause };
  // A parked run is waiting on a person, so the sentence is the run's own
  // waiting word ("waiting on approval", "parked"), not a working clause.
  if (mode === "waiting")
    return { label: "Waiting", sentence: `${workerName ?? "The run"} is ${clause}` };
  const when = endClock ? ` at ${endClock}` : "";
  if (mode === "done")
    return { label: "Done", sentence: `${workerName ?? "The run"} finished${when}` };
  if (candidateReady)
    return { label: "Candidate ready", sentence: "Candidate saved · publication pending" };
  return { label: "Stopped", sentence: `${workerName ?? "The run"} stopped${when}` };
}

/** The pane's mode class, one word per mode. */
const MODE_CLASS: Record<LiveStreamMode, string> = {
  live: "is-live",
  waiting: "is-waiting",
  done: "is-done",
  stopped: "is-stopped",
};

/** Done and waiting read the same way: the run's last words, at rest. */
function atRest(mode: LiveStreamMode): boolean {
  return mode === "done" || mode === "waiting";
}

/**
 * The clock the pane counts to. A live run counts to now; an ended run to
 * its end; a waiting run has no end yet, so its clock stops where the run
 * did, the moment it last moved.
 */
export function endClockMs(
  mode: LiveStreamMode,
  finishedAt: string | null,
  lastActivityAt: string | null,
  nowMs: number = Date.now(),
): number {
  if (mode === "live") return nowMs;
  if (finishedAt) return Date.parse(finishedAt);
  if (mode === "waiting" && lastActivityAt) return Date.parse(lastActivityAt);
  return nowMs;
}

/**
 * The present sentence of a run that has not stopped: its newest event, else
 * why there is nothing to show (nothing recorded, still queued, lining up).
 */
function restSentence(
  mode: LiveStreamMode,
  now: LiveNow | null,
  queued: boolean,
  startedAt: string | null,
): ReactNode {
  if (now) {
    return (
      <>
        {now.verb}
        {now.object ? <code className="ls-code">{now.object}</code> : null}
      </>
    );
  }
  if (atRest(mode)) return "Nothing was recorded.";
  if (queued) {
    return `Waiting for a worker to pick this up. Queued since ${hhmm(startedAt) ?? "it started"}; nothing has run yet.`;
  }
  return "Lining up. Nothing has been recorded yet.";
}

/**
 * The sentence for a live run whose record could not be read: the run is
 * working and how many tool calls it has recorded, never "Lining up. Nothing
 * has been recorded yet." from a log the pane never saw. The count is the
 * run's whole record (`runs.tool_calls`), the attempt's, not only the
 * session's. That the read failed is the line under it, so it is said once.
 */
export function floorSentence(toolCalls: number | undefined): string {
  const calls =
    typeof toolCalls === "number" && toolCalls > 0
      ? ` · ${toolCalls} tool call${toolCalls === 1 ? "" : "s"} recorded`
      : "";
  return `The session is working${calls}`;
}

/**
 * A lane run whose Machine is still preparing, in words (YAG-1138): the
 * sentence the pane leads with, the line under it and the footer's state.
 * A cold first build installs for minutes before its session starts, so
 * "Lining up" and "No activity" read as stuck over a Machine at work. Once
 * the Machine is ready, the session is starting.
 */
export function preparingWords(
  preparing: NonNullable<WorkItemRunListEntry["preparing"]>,
  nowMs: number = Date.now(),
): { sentence: string; sub: string; foot: string } {
  const sinceMs = Date.parse(preparing.since);
  const readyMs = preparing.readyAt ? Date.parse(preparing.readyAt) : Number.NaN;
  if (Number.isFinite(readyMs)) {
    const took = Number.isFinite(sinceMs)
      ? `prepared in ${ageWords((readyMs - sinceMs) / 1000)}`
      : null;
    return {
      sentence: "The Machine is ready; starting the session",
      sub: [`ready at ${hhmmssOf(readyMs)}`, took].filter(Boolean).join(" · "),
      foot: "starting the session",
    };
  }
  const since = hhmmssOf(sinceMs);
  return {
    // A session-lane turn takes up the Machine an earlier turn kept, which
    // installs nothing unless it is gone; the sentence claims no install.
    sentence: preparing.kept
      ? "Preparing the Machine the run kept"
      : "Preparing the Machine: installing tools and dependencies",
    sub: [
      since ? `since ${since}` : null,
      Number.isFinite(sinceMs) ? ageWords((nowMs - sinceMs) / 1000) : null,
    ]
      .filter(Boolean)
      .join(" · "),
    foot: "preparing the Machine",
  };
}

/**
 * The small line under the sentence: the step's own clock while live, the
 * end clock once done, and on an infrastructure stop the note that a new
 * build is one click away. Null when there is nothing to say.
 */
function underLine(input: {
  mode: LiveStreamMode;
  now: LiveNow | null;
  ageSeconds: number | null;
  doneClock: string | null;
  stopReason: string | null;
  commitCount: number;
}): { testId: "live-stream-sub" | "live-stream-infra"; text: string } | null {
  const { mode, now, ageSeconds, doneClock, stopReason, commitCount } = input;
  if (mode === "live") return { testId: "live-stream-sub", text: liveSubLine(now, ageSeconds) };
  if (atRest(mode)) return doneClock ? { testId: "live-stream-sub", text: doneClock } : null;
  if (!isInfraStop(stopReason)) return null;
  const from = commitCount > 0 ? "on the kept branch" : "from the approved plan";
  return {
    testId: "live-stream-infra",
    text: `infrastructure stop, not a plan problem · Run again starts a new build ${from}`,
  };
}

/**
 * The footer's state words: what the pane is doing and how much it holds. A
 * run that calls tools counts its actions, because a build's single long turn
 * is dozens of them and "1 turn" read as a stall (Updater, 2026-10-07).
 */
function footState(
  mode: LiveStreamMode,
  queued: boolean,
  turnCount: number,
  actionCount: number,
  endClock: string | null,
  scope: "run" | "session" = "run",
  reading = false,
): string {
  const counted = actionCount > 0 ? actionsWord(actionCount) : turnsWord(turnCount);
  // Before the record is read there is nothing to count, not zero.
  const turns = reading ? "reading the record" : `${counted} this ${scope}`;
  if (mode === "live") return queued ? "queued · waiting for a worker" : `streaming · ${turns}`;
  if (mode === "waiting") return `waiting · ${turns}`;
  return `frozen at ${endClock ?? "the end"} · ${turns}`;
}

/**
 * The line under the head: what this run was for, when it started, and what
 * it has cost. "Plan · again, after your answers · started 18:51 · $0.04 so
 * far" while it runs; the price drops "so far" once it has ended, and drops
 * out entirely when nothing was charged.
 */
export function runSubLine({
  label,
  startedAt,
  sellMillicents,
  live,
  attemptCost = false,
}: {
  label: string;
  startedAt: string | null | undefined;
  sellMillicents: number | null | undefined;
  live: boolean;
  /**
   * The start is the current session's and the price the whole attempt's (a
   * one-attempt lane run): the price says whose it is, so a fix round does
   * not read as costing everything the attempt spent (YAG-1079).
   */
  attemptCost?: boolean;
}): string {
  const started = hhmm(startedAt);
  const sell = formatMillicents(sellMillicents);
  const priced = attemptCost
    ? `${sell} on this attempt${live ? " so far" : ""}`
    : live
      ? `${sell} so far`
      : sell;
  return [label, started ? `started ${started}` : null, sell ? priced : null]
    .filter(Boolean)
    .join(" · ");
}

/* ── clocks. Machine facts, in the machine's own 24h voice ── */

export function hhmm(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return null;
  return when.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** HH:MM:SS from an epoch-ms stamp. Null for an event that recorded no time. */
function hhmmssOf(ms: number | null | undefined): string | null {
  if (ms == null || !Number.isFinite(ms)) return null;
  const when = new Date(ms);
  if (Number.isNaN(when.getTime())) return null;
  return when.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

/** Seconds as `m:ss`, or `h:mm:ss` once an attempt has run past the hour. */
export function durationClock(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const s = String(total % 60).padStart(2, "0");
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}:${s}`;
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}:${s}`;
}

/**
 * How long a live run may sit with nothing recorded and no activity stamped
 * before the card says it is waiting for a worker rather than "lining up".
 * Every run activity stamps within its first minute.
 */
export const QUEUED_NOTICE_SECONDS = 90;

/* ── the newest event, as a sentence ──
   The verb comes from the tool the run called (`TOOL_VERB`, shared with the
   conversation's tool rows), and from nowhere else. */

/** How much of a prose turn the one-line "now" sentence carries. */
export const NOW_SENTENCE_MAX = 160;

/**
 * A prose turn as the one sentence the head reads: markdown stripped, the
 * first sentence when it ends inside the ceiling, else cut at a word. The
 * whole turn is in the conversation below; this line is only the glance.
 */
export function nowSentenceOf(text: string): string {
  const plain = stripMarkdownInline(text);
  if (plain.length <= NOW_SENTENCE_MAX) return plain;
  const sentence = /^(.{1,159}?[.!?])(?:\s|$)/.exec(plain);
  return sentence?.[1] ?? clampToWord(plain, NOW_SENTENCE_MAX);
}

export interface LiveNow {
  /** The verb half, plain text. */
  verb: string;
  /** The machine half, set in `<code>`. Null when the event has no object. */
  object: string | null;
  /** The kind word for the sub line: the tool name, else the event kind. */
  kind: string;
  /** When it happened, ms. */
  at: number;
  /**
   * Set on a planning progress beat: the short phrase the sub line counts the
   * step's time against ("Checking the plan · 1m 20s"). Null on a beat that
   * ends the work, and absent on every other event.
   */
  step?: string | null;
  /** True for a planning progress beat, so the sub line prints no kind word. */
  milestone?: boolean;
  /** True for a build check that is running: its step is bounded by {@link checkRunningSince}. */
  check?: boolean;
}

/**
 * "1m 20s" since a beat, for the sub line while the step it named runs. A beat
 * stamped ahead of this browser's clock (server/client drift) reads "0s" until
 * the clocks agree, rather than a negative time; the step name beside it still
 * says what is running, and the next beat corrects it.
 */
export function sinceLabel(at: number, now: number = Date.now()): string {
  if (!Number.isFinite(at)) return "0s";
  return durationLabel(new Date(at).toISOString(), new Date(now).toISOString()) ?? "0s";
}

/** "12s", "6m 4s", "1h 5m": an age, to the second while it is short. */
export function ageWords(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m ${total % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/**
 * The live sub line under the "now" sentence. A planning beat with a step
 * counts that step's time ("14:22:05 · Checking the plan · 1m 20s"), so a
 * person watching sees it move between beats; everything else says when the
 * headline happened and how long since the run last did anything, ticking.
 */
export function liveSubLine(
  now: LiveNow | null,
  ageSeconds: number | null,
  at: number = Date.now(),
): string {
  if (now?.step)
    return [hhmmssOf(now.at), now.step, sinceLabel(now.at, at)].filter(Boolean).join(" · ");
  return [
    hhmmssOf(now?.at),
    ageSeconds != null ? `last activity ${ageWords(ageSeconds)} ago` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * How long a live run may record nothing before the card says so. A build
 * calls a tool every few seconds and a long test suite runs for a minute or
 * two; three minutes of nothing is worth a sentence, not an alarm.
 */
export const QUIET_NOTICE_SECONDS = 180;

/** A run's time limit in a word: "2h", "45m". */
function limitWords(seconds: number): string {
  if (seconds >= 3600) return `${Math.round(seconds / 360) / 10}h`;
  return `${Math.round(seconds / 60)}m`;
}

/**
 * The honest line for a quiet run: how long nothing was recorded, and that
 * the run is past its limit when it is. Null while the run is recording.
 */
export function quietNotice(input: {
  quietSeconds: number | null;
  elapsedSeconds: number | null;
  budgetSeconds: number | null;
  noun: string;
}): string | null {
  const { quietSeconds, elapsedSeconds, budgetSeconds, noun } = input;
  if (quietSeconds == null || quietSeconds < QUIET_NOTICE_SECONDS) return null;
  const minutes = Math.floor(quietSeconds / 60);
  const lead = `No activity for ${minutes} minute${minutes === 1 ? "" : "s"}`;
  // The limit is a timeout, not an estimate: naming it while within it read
  // as "this will take six hours" (2026-10-12). Past it is news; within it is not.
  if (budgetSeconds != null && elapsedSeconds != null && elapsedSeconds > budgetSeconds) {
    return `${lead}; the ${noun} is past its ${limitWords(budgetSeconds)} limit`;
  }
  return `${lead}; the ${noun} has not stopped`;
}

/** The test counts a build's result carries ("7 passed, 1 failed"), as opposed to ok / failed. */
const TEST_COUNTS = /^\d+ (passed|failed|errors?|skipped)\b/;

/** The call a result answers: the newest call of the same tool before it. */
function callFor(events: readonly CodingEvent[], index: number, tool: string): string | null {
  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    const event = events[cursor];
    if (event?.kind === "tool_call" && event.tool === tool)
      return event.argsSummary?.trim() || null;
  }
  return null;
}

/**
 * A result as what was done: its test counts when it ran tests, else the
 * call it answers in the past tense ("Ran", "Edited"), or that it failed.
 */
function toolResultNowOf(
  events: readonly CodingEvent[],
  index: number,
  event: Extract<CodingEvent, { kind: "tool_result" }>,
): LiveNow {
  const summary = event.summary?.trim() ?? "";
  const base = { kind: event.tool, at: event.at };
  // A search that found nothing reads as what it was, not as a failure: the
  // line carries the fixed words "no matches" and never the word "failed".
  if (event.outcome === "no_match") {
    const object = callFor(events, index, event.tool);
    return { ...base, verb: "Searched with no matches:", object };
  }
  if (TEST_COUNTS.test(summary)) return { ...base, verb: `Tests: ${summary}`, object: null };
  const object =
    callFor(events, index, event.tool) ??
    (summary && summary !== (event.ok ? "ok" : "failed") ? summary : null);
  if (event.ok) return { ...base, verb: TOOL_PAST[event.tool] ?? `Finished ${event.tool}`, object };
  const command = event.tool === "bash" || event.tool === "run_command";
  return {
    ...base,
    verb: command ? "Command failed:" : `${TOOL_VERB[event.tool] ?? event.tool} failed:`,
    object,
  };
}

/**
 * The newest event that has words of its own, as what the run is doing now.
 * Null for a run that has said nothing. A result reads as what was done ("Ran
 * `pytest`", "Tests: 7 passed, 1 failed"); a stage beat reads as a sentence,
 * never as its key, and names the plan step when the run reports one.
 */
export function liveNowOf(
  events: readonly CodingEvent[],
  steps: readonly WorkRunStep[] = [],
): LiveNow | null {
  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index];
    if (!event) continue;
    switch (event.kind) {
      case "tool_call": {
        const verb = TOOL_VERB[event.tool] ?? event.tool;
        const summary = event.argsSummary?.trim();
        return {
          verb,
          object: summary ? summary : null,
          kind: event.tool,
          at: event.at,
        };
      }
      case "tool_result":
        return toolResultNowOf(events, index, event);
      case "message":
        // A build check that is running, as what runs: the sub line counts
        // its time ("Checks · 4m 12s"). Its end reads as the sentence it carries.
        if (event.check?.phase === "start") {
          return {
            verb: event.check.base ? "Running checks on the unchanged code:" : "Running checks:",
            object: event.check.command,
            kind: "message",
            at: event.at,
            step: BUILD_CHECK_STEP_LABEL,
            milestone: true,
            check: true,
          };
        }
        // A subagent at work, as which one and its last action
        // ("Subagent implementer: edit app.ts") (YAG-1095).
        if (event.subagent?.action) {
          return {
            verb: `Subagent ${event.subagent.agent}:`,
            object: event.subagent.action,
            kind: "message",
            at: event.at,
          };
        }
        // A build's setup beat, the same way: its rung's label is what the
        // sub line counts time against. The ready beat ends setup and has no
        // label, so it reads as the plain sentence it carries.
        if (isBuildSetupStep(event.setupStep)) {
          const label = BUILD_SETUP_STEP_COPY[event.setupStep].label;
          if (label) {
            return {
              verb: nowSentenceOf(event.text),
              object: null,
              kind: "message",
              at: event.at,
              step: label,
              milestone: true,
            };
          }
        }
        // A planning progress beat is the agent's own plain sentence; its
        // step is what the sub line counts time against. A beat this bundle
        // does not know still reads as the sentence it carries.
        if (isPlanningMilestone(event.milestone)) {
          return {
            verb: nowSentenceOf(event.text),
            object: null,
            kind: "message",
            at: event.at,
            step: PLANNING_MILESTONE_COPY[event.milestone].label,
            milestone: true,
          };
        }
        return { verb: nowSentenceOf(event.text), object: null, kind: "message", at: event.at };
      case "thinking":
        // A bare tick has no words; a thought with text is the model thinking.
        if (!event.text?.trim()) continue;
        return { verb: "Thinking", object: null, kind: "thinking", at: event.at };
      case "stage": {
        const current =
          event.stage === "implement" && event.phase === "start"
            ? steps.find((step) => step.state === "current")
            : undefined;
        return {
          verb: current
            ? `Implementing step ${current.sequence} of ${steps.length}`
            : stagePhrase(event),
          object: null,
          kind: "stage",
          at: event.at,
        };
      }
      case "completed":
        return {
          verb: "Finished on",
          object: event.branch,
          kind: "completed",
          at: event.at,
        };
      case "error":
        return { verb: "Error", object: event.message, kind: "error", at: event.at };
      default:
        // usage and the rest: no words of their own to print.
        continue;
    }
  }
  return null;
}

/**
 * How long a running check counts as activity: the longest the build's gate
 * lets one command run. A session that died mid-check never writes its end,
 * and past this the card says the build has gone quiet.
 */
export const CHECK_RUNNING_MAX_SECONDS = 20 * 60;

/**
 * When the build check that is running started: the newest check beat, when
 * it is a start; null when none runs. A check runs for minutes with no other
 * word, and the review's lenses can finish while one still runs; either way
 * the build is not quiet (YAG-1031, the card read "No activity for 10
 * minutes" through the gate's checks).
 */
export function checkRunningSince(events: readonly CodingEvent[]): number | null {
  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index];
    if (event?.kind === "message" && event.check) {
      return event.check.phase === "start" ? event.at : null;
    }
  }
  return null;
}

/** How much of a steering note's first line the pane's line quotes. */
export const STEERING_FIRST_LINE_MAX = 80;

/**
 * A steering note as the pane's line quotes it: its first line with words in
 * it, markdown stripped and the closing period dropped (the line closes the
 * quote itself), cut at a word past {@link STEERING_FIRST_LINE_MAX}.
 */
export function steeringNoteFirstLine(body: string): string {
  const line =
    body
      .split(/\r?\n/)
      .map((text) => stripMarkdownInline(text))
      .find((text) => text !== "") ?? "";
  return clampToWord(line.replace(/[.\s]+$/, ""), STEERING_FIRST_LINE_MAX);
}

/**
 * Has the feed shown this note reaching the session? A steering-note beat at
 * or after the note was left is its read: a note left after the last beat is
 * still waiting. Both times are the database's.
 */
export function noteHandedOver(events: readonly CodingEvent[], createdAt: string): boolean {
  const left = Date.parse(createdAt);
  if (!Number.isFinite(left)) return false;
  return events.some((event) => isSteeringNoteEvent(event) && event.at >= left);
}

function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

type RunArtifactLike = {
  kind: string;
  url?: string | null;
  label: string;
  prNumber?: number | null;
};

/** The one line the Outputs fold caps with. */
export function prLabelOf(prNumber: number | null | undefined, pr: RunArtifactLike | null): string {
  if (prNumber != null) return `PR #${prNumber}`;
  if (pr?.prNumber != null) return `PR #${pr.prNumber}`;
  return pr ? "PR opened" : "no PR yet";
}

/**
 * Infrastructure stops: the machine's fault, never the plan's. A timeout is
 * deliberately NOT one: a run that used its whole budget may well have a plan
 * problem, and this surface must not tell the reader otherwise.
 */
function isInfraStop(reason: string | null | undefined): boolean {
  return stopReasonClass(reason) === "infra";
}

/**
 * A stopped build's classified cause in words (`runs.stop_cause`), or null
 * when the run carries none or the classifier could not name one, so the
 * stop code's own words stand.
 */
function stopCauseWords(cause: string | null | undefined): string | null {
  if (!isBuildStopCause(cause) || cause === "unknown") return null;
  return BUILD_STOP_CAUSE_WORDS[cause];
}

/**
 * The "Why it stopped" sentence, without its closing period: the build's
 * classified cause when it has one, else the shared words for the run's stop
 * code (the same words the row and the pill print), else the run's own
 * outcome summary, else "Stopped".
 */
function stopSentenceOf(
  reason: string | null,
  outcomeSummary: string | null | undefined,
  cause?: string | null,
): string {
  const caused = stopCauseWords(cause);
  if (caused) return sentenceCase(caused);
  const words = stopReasonWords(reason);
  if (words !== "stopped") return sentenceCase(words);
  const summary = outcomeSummary?.trim().replace(/[.\s]+$/, "");
  return summary ? sentenceCase(summary) : "Stopped";
}

/* ── the pane ── */

export function LiveStream({
  workItemId,
  run,
  runs,
  workerName,
  mode,
  stage,
  prNumber,
  paneRef,
  record = false,
  children,
  environment = null,
  laneSpeakers = null,
}: LiveStreamProps) {
  // Whether this attempt's record has been read. Until the first read lands,
  // an empty log is "not read yet": the pane said "Lining up", "No activity
  // for 40 minutes" and "0 turns" over a session that was working, until the
  // read landed (YAG-1054, 2026-10-26). A failed first read is its own state.
  const [readState, setReadState] = useState<"reading" | "read" | "failed">("reading");
  const { view, reload: reloadView } = useWorkRunView(workItemId, true);
  // A steering note left from the sheet or the chat changes the item, and the
  // view says whether one waits (YAG-1127).
  useWorkItemEntityChange(workItemId, reloadView);
  const runId = run?.id ?? view.run?.id ?? null;
  // The view reads the item's latest EXECUTION run. When the pane shows a
  // different run (a new planning run), the view's heartbeat and steps
  // belong to that older run and must not leak onto this one.
  const viewIsOtherRun = view.run != null && view.run.id !== runId;
  const viewRun = viewIsOtherRun ? null : view.run;
  const viewSteps = viewIsOtherRun ? [] : view.steps;
  const events = useRunEvents(workItemId, {
    runId,
    live: mode === "live",
    onRead: () => setReadState("read"),
    // A failed read is not an empty record: the pane says it could not read
    // it, never "Lining up" over a run that may be working.
    onError: () => setReadState("failed"),
  });
  // Another attempt's record has not been read yet.
  useEffect(() => {
    setReadState("reading");
  }, [workItemId, runId]);
  // An event that arrived over the socket is something to show either way.
  const reading = readState === "reading" && events.length === 0;
  const readFailed = readState === "failed" && events.length === 0;
  const commits = useMemo(() => runCommits(events), [events]);
  // A one-attempt lane run is every session of its attempt, and its own start
  // is the attempt's: one whose first sessions died sat on a Run again ask for
  // hours, and its card counted those hours over a plan session fifteen
  // minutes old (NEARM-523, 2026-10-24). While it runs, the head, the clock and
  // the counts follow the current session; the conversation keeps them all.
  const lane = run?.lane === "attempt";
  const sessionStartedAt = mode === "live" ? laneSessionStartOf(run) : null;
  const sessionStartMs = sessionStartedAt ? Date.parse(sessionStartedAt) : null;
  // What the session itself did: a steering note reaching it is a row of the
  // conversation, never what Wright is doing now, an action or activity.
  const current = useMemo(
    () => sessionEventsOf(events, sessionStartMs).filter((event) => !isSteeringNoteEvent(event)),
    [events, sessionStartMs],
  );
  const now = useMemo(() => liveNowOf(current, viewSteps), [current, viewSteps]);
  // The footer counts what the conversation shows, turns, not raw events: a
  // planning run's "1 events" read as a stall when it was one beat. A run that
  // calls tools counts those instead, so a long turn visibly grows. A build's
  // setup beats are not turns either: before Wright's first turn they read
  // "6 turns" for a build that had not started yet. A subagent's beat is not
  // a turn either: it says the build is moving (YAG-1095).
  const turnCount = useMemo(() => {
    const countable = current.filter(
      (event) =>
        !(
          event.kind === "message" &&
          (isBuildSetupStep(event.setupStep) || event.check || event.subagent)
        ),
    );
    const { turns, hidden } = talkTurnsOf(countable);
    return turns.length + hidden;
  }, [current]);
  const actionCount = useMemo(
    () => current.filter((event) => event.kind === "tool_call").length,
    [current],
  );
  const newestAt = current[current.length - 1]?.at;
  const showsCost = useShowsRunCost();

  const live = mode === "live";
  const waiting = mode === "waiting";
  // A lane run whose Machine is still preparing says so, from the runs list's
  // own record, whatever the events read holds (YAG-1138). Once the Machine
  // is ready, quiet counts from then: its install was not the session's quiet.
  const preparing = live && lane ? (run?.preparing ?? null) : null;
  const preparingLine = preparing ? preparingWords(preparing) : null;
  const machineReadyMs = preparing?.readyAt ? Date.parse(preparing.readyAt) : Number.NaN;
  const machineReady = Number.isFinite(machineReadyMs);
  const done = atRest(mode);
  // The steering note waiting for the next step (YAG-1127). It goes the moment
  // the feed shows it reached Wright, and the view's next read settles it: on
  // the runs poll's beat while one waits, so it never needs a reload.
  const pendingNote = view.pendingSteeringNote ?? null;
  const steering =
    (live || waiting) && pendingNote && !noteHandedOver(events, pendingNote.createdAt)
      ? pendingNote
      : null;
  const pendingNoteId = pendingNote?.id ?? null;
  useEffect(() => {
    if (!live || !pendingNoteId) return;
    const timer = setInterval(reloadView, LIVE_RUN_POLL_MS);
    return () => clearInterval(timer);
  }, [live, pendingNoteId, reloadView]);
  const startedAt = sessionStartedAt ?? run?.startedAt ?? viewRun?.startedAt ?? null;
  const finishedAt = run?.finishedAt ?? viewRun?.finishedAt ?? null;
  const budgetSeconds = run?.budgetSeconds ?? viewRun?.budgetSeconds ?? null;
  const lastActivityAt = run?.lastActivityAt ?? viewRun?.lastActivityAt ?? null;
  const attemptNo = run?.attemptNo ?? viewRun?.attemptNo ?? 1;

  // The second hand. It runs only while the run does: an ended pane is a
  // record, and a record does not tick.
  const [, tick] = useState(0);
  useEffect(() => {
    if (!live) return;
    const timer = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [live]);

  const startedMs = startedAt ? Date.parse(startedAt) : Number.NaN;
  const endMs = endClockMs(mode, finishedAt, lastActivityAt);
  const elapsed = Number.isFinite(startedMs) ? (endMs - startedMs) / 1000 : null;
  // Last activity is the newest thing the run RECORDED, and nothing else. The
  // run's heartbeat also moves while a sandbox is merely held (NEARM-428 read
  // "57s ago" for minutes), so a run that has recorded nothing prints no
  // "last activity" at all: the line and the quiet notice below read the one
  // moment and can never disagree (NEARM-526 printed "last activity 51s ago"
  // directly above "No activity for 31 minutes").
  const recorded = typeof newestAt === "number" && Number.isFinite(newestAt);
  const ageSeconds = recorded ? Math.max(0, Math.round((Date.now() - newestAt) / 1000)) : null;
  // Nothing recorded, nothing stamped, and long past the first minute: the
  // run is still in the queue, not starting. Staging, 2026-10-05: a planning
  // run sat 8 minutes behind a starved worker reading "Lining up · 0 events".
  const queued =
    live &&
    !preparing &&
    !now &&
    !lastActivityAt &&
    elapsed != null &&
    elapsed >= QUEUED_NOTICE_SECONDS;
  // Quiet is measured from the newest recorded event, or from the start when
  // nothing is recorded: the heartbeat never counts as activity here. A
  // planning step's own clock already shows it moving between beats, and a
  // build waiting on its repository's setup says so on its environment line.
  const quietSeconds = recorded
    ? ageSeconds
    : machineReady
      ? Math.max(0, (Date.now() - machineReadyMs) / 1000)
      : elapsed;
  const checkStartedAt = useMemo(() => checkRunningSince(current), [current]);
  const checkRunning =
    checkStartedAt !== null && (Date.now() - checkStartedAt) / 1000 < CHECK_RUNNING_MAX_SECONDS;
  const stepRunning = Boolean(now?.step) && !now?.check;
  // A live run whose record could not be read says what its own record holds
  // (`runs.tool_calls`, refreshed by the runs poll) instead of "Lining up" or
  // "No activity". The heartbeat is deliberately not the signal (NEARM-428:
  // it moves while a sandbox is merely held).
  const toolCalls = run?.toolCalls ?? viewRun?.toolCalls ?? undefined;
  const floor = live && readFailed;
  const quiet =
    live &&
    !reading &&
    !queued &&
    !stepRunning &&
    !checkRunning &&
    !floor &&
    !(preparing && !machineReady) &&
    environment?.source !== "waiting"
      ? quietNotice({
          quietSeconds,
          elapsedSeconds: elapsed,
          budgetSeconds,
          noun: (run?.purpose ?? viewRun?.purpose) === "execution" ? "build" : "run",
        })
      : null;

  const stopReason = run?.stopReason ?? run?.failureReason ?? null;
  const branch = commits[commits.length - 1]?.branch ?? null;
  const artifacts = viewRun?.artifacts ?? [];
  const candidate = artifacts.find((artifact) => artifact.kind === "code_candidate");
  const sandbox = artifacts.find((artifact) => artifact.kind === "sandbox") ?? null;
  const pr = artifacts.find((artifact) => artifact.kind === "pr") ?? null;
  const prLabel = prLabelOf(prNumber, pr);
  const candidateReady =
    mode === "stopped" &&
    !!candidate &&
    stopReason === "pilot_evaluation" &&
    !pr &&
    prNumber == null;

  const clause = waiting && run ? caseRunStatusLabel(run) : liveClauseOf(stage, run, workerName);
  const endClock = hhmm(finishedAt);
  const heading = streamHeading(mode, candidateReady, clause, workerName, endClock);
  const subLine = run
    ? runSubLine({
        label: runLabel(run, runs),
        startedAt,
        // Null on the pull-request plan: the sub-line drops the price.
        sellMillicents: showsCost ? run.sellMillicents : null,
        live,
        // The clock is the session's, but the price is the whole attempt's.
        attemptCost: sessionStartedAt != null,
      })
    : null;
  // A lane run's sessions are different Agents': each turn names whose it is.
  const sessions = lane ? run?.sessions : undefined;
  const speakerAt =
    laneSpeakers && sessions && sessions.length > 0
      ? (at: number) => {
          const session = sessionAtOf(sessions, at);
          if (!session) return null;
          return session.stage === "plan" ? laneSpeakers.plan : laneSpeakers.build;
        }
      : undefined;
  const doneClock = done && now ? hhmmssOf(now.at) : null;
  // What the session's clock leaves out: when the attempt began, and the
  // sessions it ran before this one.
  const earlier = sessionStartedAt ? (run?.earlierSessions ?? 0) : 0;
  const attemptStarted = hhmm(run?.startedAt);
  const attemptLine =
    earlier > 0
      ? [
          attemptStarted ? `attempt started ${attemptStarted}` : null,
          `${earlier} earlier session${earlier === 1 ? "" : "s"}`,
        ]
          .filter(Boolean)
          .join(" · ")
      : null;
  const under = preparingLine
    ? { testId: "live-stream-sub" as const, text: preparingLine.sub }
    : underLine({
        mode,
        now,
        ageSeconds,
        doneClock,
        stopReason,
        commitCount: commits.length,
      });

  // A beat that names another run of this item (a reused reading) links to
  // that run's record, by the number its URL carries.
  const linkForRun = (otherRunId: string): string | null => {
    const other = runs.find((entry) => entry.id === otherRunId);
    return other ? runUrl(workItemId, other.attemptNo) : null;
  };

  return (
    <div
      className={`ls ${MODE_CLASS[mode]}${record ? " is-record" : ""}`}
      data-testid="live-stream"
      data-mode={mode}
      ref={paneRef}
    >
      <div className="ls-head">
        <span className="ls-pill" data-testid="live-stream-pill">
          {live && <span className="ls-dot" aria-hidden />}
          {heading.label}
        </span>
        <span className="ls-who">{heading.sentence}</span>
        <span className="ls-clock" data-testid="live-stream-clock">
          {elapsed != null ? durationClock(elapsed) : "—"}
        </span>
        {subLine && (
          <div className="ls-run" data-testid="live-stream-run">
            {subLine}
          </div>
        )}
        {attemptLine && (
          <div className="ls-run" data-testid="live-stream-attempt">
            {attemptLine}
          </div>
        )}
        {environment && (
          <div
            className="ls-env"
            data-testid="live-stream-environment"
            data-source={environment.source}
          >
            {buildEnvironmentLine(environment)}
          </div>
        )}
      </div>

      {lane ? (
        <LaneProgress run={run} events={events} live={live} nowMs={endMs} />
      ) : (
        <>
          <PlanningLadder run={run ?? viewRun} events={events} live={live} nowMs={endMs} />
          <SetupLadder run={run ?? viewRun} events={events} live={live} nowMs={endMs} />
        </>
      )}

      <div className="ls-now" data-testid="live-stream-now">
        {mode === "stopped" && (
          <div className="ls-why-lab">{candidateReady ? "What is ready" : "Why it stopped"}</div>
        )}
        {mode === "stopped" ? (
          <p className="ls-sentence is-stopped-line">
            {candidateReady
              ? "Code candidate saved for review; publication has not completed"
              : stopSentenceOf(stopReason, run?.outcomeSummary, run?.stopCause)}
            .
            {commits.length > 0
              ? ` Branch kept with ${commits.length} commit${commits.length === 1 ? "" : "s"}.`
              : candidate
                ? " Candidate changes are preserved."
                : reading
                  ? ""
                  : " No commits were recorded."}
            {pr || prNumber != null ? "" : " No PR was opened."}
          </p>
        ) : (
          <p className="ls-sentence">
            {preparingLine
              ? preparingLine.sentence
              : reading
                ? "Reading what has been recorded."
                : floor
                  ? floorSentence(toolCalls)
                  : restSentence(mode, now, queued, startedAt)}
          </p>
        )}
        {under && (
          <div className="ls-sub" data-testid={under.testId}>
            {under.text}
          </div>
        )}
        {floor && (
          <div className="ls-sub" data-testid="live-stream-floor" role="status">
            The live view could not be read.
          </div>
        )}
        {quiet && (
          <div className="ls-sub ls-quiet" data-testid="live-stream-quiet" role="status">
            {quiet}
          </div>
        )}
        {steering && (
          <div
            className="ls-sub ls-steer"
            data-testid="live-stream-steering"
            title={steeringNoteLeftBy(steering.authorName, hhmm(steering.createdAt))}
          >
            {steeringNoteWaitingLine(steeringNoteFirstLine(steering.body))}
          </div>
        )}
      </div>

      <LiveTalk
        events={events}
        workerName={workerName}
        live={live}
        limit={record ? Number.POSITIVE_INFINITY : TALK_TURN_LIMIT}
        linkForRun={linkForRun}
        {...(speakerAt ? { speakerAt } : {})}
      />

      <div className="ls-foot">
        <span className="ls-foot-state">
          {live && (
            <>
              <Loader2 width={11} height={11} className="wk-spin" aria-hidden />{" "}
            </>
          )}
          {preparingLine
            ? preparingLine.foot
            : footState(
                mode,
                queued,
                turnCount,
                actionCount,
                endClock,
                sessionStartedAt ? "session" : "run",
                reading,
              )}
        </span>
        <span className="ls-foot-links">
          {!record && (
            <RouterLink className="ls-link" to={runUrl(workItemId, attemptNo)}>
              open the run record
            </RouterLink>
          )}
          {sandbox?.url && (
            <a className="ls-link" href={sandbox.url} target="_blank" rel="noreferrer">
              sandbox
            </a>
          )}
        </span>
      </div>

      <div className="ls-more">
        <StepsFold steps={viewSteps} open={record} live={live} />
        <OutputsFold
          branch={branch}
          commits={commits}
          artifacts={artifacts}
          prLabel={prLabel}
          prKnown={prNumber != null || pr != null}
          open={record}
        />
        <EarlierRunsFold workItemId={workItemId} runId={runId} runs={runs} open={record} />
        {children}
      </div>
    </div>
  );
}

/** "1 turn", "12 turns". */
function turnsWord(count: number): string {
  return `${count} turn${count === 1 ? "" : "s"}`;
}

/** "1 action", "14 actions". */
function actionsWord(count: number): string {
  return `${count} action${count === 1 ? "" : "s"}`;
}

/* ── the folds. Each one is one question, answered from a read that already exists. ── */

/** "6 steps": a live run's step count while it reports no progress. */
function stepCountWords(count: number): string {
  return `${count} ${count === 1 ? "step" : "steps"}`;
}

/** Where a live run's step progress shows, while it reports none. */
export const STEPS_UNREPORTED_CAP = "marked done as the build reports them";

function StepsFold({
  steps,
  open,
  live,
}: {
  steps: WorkRunViewResponse["steps"];
  open?: boolean;
  live: boolean;
}) {
  if (steps.length === 0) return null;
  const done = steps.filter((step) => step.state === "done").length;
  // "current" is the only state a journal actually writes, so only it is
  // "now". The first step not done is only "next": a build that reports no
  // step progress must not claim to be on step 1 for its whole run.
  const current = steps.find((step) => step.state === "current") ?? null;
  const next = current ? null : (steps.find((step) => step.state === "not_started") ?? null);
  // A build settles its steps as it reports their stage done, never before:
  // until the first report every step reads not started. "0 of 6 done" over a
  // build that had built all six (staging, 2026-10-21) counted a journal
  // nobody had written yet, so a live run with no step progress says how many
  // steps there are, not how many are done.
  const unreported = live && done === 0 && current == null;
  return (
    <Fold
      testId="live-stream-steps"
      word="Steps"
      rest={unreported ? stepCountWords(steps.length) : `${done} of ${steps.length} done`}
      cap={
        unreported
          ? STEPS_UNREPORTED_CAP
          : current
            ? `now: ${current.description}`
            : next
              ? `next: ${next.description}`
              : ""
      }
      open={open}
    >
      <ol className="wk-run-steps">
        {steps.map((step) => (
          <li
            key={step.sequence}
            className={`wk-run-step is-${step.state.replace("_", "-")}`}
            data-state={step.state}
          >
            <span className="wk-run-step-mark" aria-hidden>
              {step.state === "done" ? "✓" : step.sequence}
            </span>
            <span className="wk-run-step-body">
              <span className="wk-run-step-desc">{step.description}</span>
            </span>
          </li>
        ))}
      </ol>
    </Fold>
  );
}

function OutputsFold({
  branch,
  commits,
  artifacts,
  prLabel,
  prKnown,
  open,
}: {
  branch: string | null;
  commits: RunCommit[];
  artifacts: readonly RunArtifactLike[];
  prLabel: string;
  prKnown: boolean;
  open?: boolean;
}) {
  const commitWord = `${commits.length} commit${commits.length === 1 ? "" : "s"}`;
  return (
    <Fold
      testId="live-stream-outputs"
      word="Outputs"
      rest={
        branch
          ? `${branch} with ${commitWord}`
          : artifacts.length
            ? `${artifacts.length} saved output${artifacts.length === 1 ? "" : "s"}`
            : "nothing yet"
      }
      cap={prLabel}
      open={open}
    >
      <div className="ls-cards">
        {commits.map((commit) => (
          <div className="ls-card" key={commit.key}>
            <span className="ls-card-lab">commit</span>
            <span className="ls-card-v">{commit.sha.slice(0, 7)}</span>
            <span className="ls-card-sub">{commit.branch}</span>
          </div>
        ))}
        {artifacts.map((artifact, index) => (
          <div className="ls-card" key={`${index}-${artifact.kind}`}>
            <span className="ls-card-lab">
              {artifact.kind === "code_candidate" ? "Candidate" : artifact.kind}
            </span>
            {artifact.url ? (
              <a className="ls-card-v" href={artifact.url} target="_blank" rel="noreferrer">
                {artifact.label}
              </a>
            ) : (
              <span className="ls-card-v">{artifact.label}</span>
            )}
          </div>
        ))}
        {!prKnown && (
          <div className="ls-card is-notyet">
            <span className="ls-card-lab">pull request</span>
            <span className="ls-card-v">not yet</span>
          </div>
        )}
      </div>
    </Fold>
  );
}

/**
 * The cap names the newest earlier run: "Plan · failed 18:40 · Worker was
 * lost during a deploy". The stop's own words ride along only when the
 * classifier has some beyond a bare "stopped".
 */
export function earlierRunsCap(
  earlier: readonly WorkItemRunListEntry[],
  runs: readonly WorkItemRunListEntry[],
): string {
  const newest = earlier[0];
  if (!newest) return "";
  const clock = hhmm(newest.finishedAt ?? newest.startedAt);
  const head = `${runLabel(newest, runs)} · ${caseRunStatusLabel(newest)}${clock ? ` ${clock}` : ""}`;
  const stopped =
    newest.status === "failed" || newest.status === "partial" || newest.status === "dismissed";
  const words = stopped
    ? (stopCauseWords(newest.stopCause) ??
      stopReasonWords(newest.stopReason ?? newest.failureReason))
    : "stopped";
  return words !== "stopped" ? `${head} · ${words}` : head;
}

/** Every other run on the item, newest first, each named by what it was for. */
function EarlierRunsFold({
  workItemId,
  runId,
  runs,
  open,
}: {
  workItemId: string;
  runId: string | null;
  runs: readonly WorkItemRunListEntry[];
  open?: boolean;
}) {
  // Earlier means earlier: on the run page a person can be reading an old
  // run, and the runs after it are in the header, not in this fold.
  const current = runs.find((entry) => entry.id === runId) ?? null;
  const earlier = runs
    .filter(
      (entry) => entry.id !== runId && (current == null || entry.attemptNo < current.attemptNo),
    )
    .sort((a, b) => b.attemptNo - a.attemptNo);
  if (earlier.length === 0) return null;
  return (
    <Fold
      testId="live-stream-earlier-runs"
      word="Earlier runs"
      rest={String(earlier.length)}
      cap={earlierRunsCap(earlier, runs)}
      open={open}
    >
      <div className="ls-attempts">
        {earlier.map((entry) => (
          <div className="ls-attempt-row" key={entry.id} data-testid="live-stream-earlier-run">
            <span className="ls-card-lab">{runLabel(entry, runs)}</span>
            <span className="ls-card-v">{caseRunStatusLabel(entry)}</span>
            <span className="ls-card-sub">{hhmm(entry.finishedAt ?? entry.startedAt) ?? ""}</span>
            <RouterLink className="ls-link" to={runUrl(workItemId, entry.attemptNo)}>
              open
            </RouterLink>
          </div>
        ))}
      </div>
    </Fold>
  );
}

/** One folded line. Native `<details>`: the browser already knows this gesture. */
export function Fold({
  word,
  rest,
  cap,
  testId,
  open,
  children,
}: {
  word: string;
  rest: string;
  cap: string;
  testId: string;
  /** Start open. The run record opens every fold; the live card opens none. */
  open?: boolean;
  children: ReactNode;
}) {
  return (
    <details className="ls-fold" data-testid={testId} open={open || undefined}>
      <summary className="ls-fold-head">
        <span className="ls-chev" aria-hidden />
        <span className="ls-fold-word">{word}</span>
        <span className="ls-fold-rest">· {rest}</span>
        {cap && <span className="ls-fold-cap">{cap}</span>}
      </summary>
      <div className="ls-fold-body">{children}</div>
    </details>
  );
}

/** One machine fact. Nothing renders for a fact the record does not carry. */
function Fact({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="ls-fact">
      <span className="ls-fact-lab">{label}</span>
      <span className="ls-fact-v">{value}</span>
    </div>
  );
}
